import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useToast } from 'use-toast-mui';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import QueryString from 'qs';
import { Box, Modal, CircularProgress, Checkbox, Tooltip } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import LoyaltyOutlinedIcon from '@mui/icons-material/LoyaltyOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import SearchIcon from '@mui/icons-material/Search';
import { errorMessages, exportMembersToStyledExcel } from '../../../../utils/methods';
import useUserAuth from '../../../../utils/useUserAuth';
import SmsModal from '../sms/SmsModal';
import GroupPointGrantModal from './GroupPointGrantModal';
import { getBranchPointSetting } from '../../../../api/member-point';
import { getUsersList } from '../../../../api/member-management';
import { MEMBER_LIST_COLUMNS } from '../../memberList/memberListColumns';
import {
  getMemberGroups,
  createMemberGroup,
  updateMemberGroup,
  deleteMemberGroup,
  getMemberGroupMembers,
  removeMemberGroupMembers,
} from '../../../../api/member-group';
import ConfirmModal from '../../../common/modal/ConfirmModal';
import GroupChangeSummary from './GroupChangeSummary';
import BetaBadge from '../../../common/BetaBadge';
import MemberGroupAttendanceTab from './MemberGroupAttendanceTab';
import MemberGroupMemberEditModal from './MemberGroupMemberEditModal';
import TicketInfoCell from '../../memberList/TicketInfoCell';
import { MemberStatusBadge } from '../../memberList/MemberListStyled';
import { centersDetail } from '../../../../api/store-management';
import MemberGroupConditionPanel, {
  EMPTY_CONDITIONS,
  countConditions,
} from './MemberGroupConditionPanel';
import GroupMoveCopyPopover from './GroupMoveCopyPopover';
import MemberGroupTransferModal from './MemberGroupTransferModal';
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalBody,
  GroupPanel,
  GroupPanelHeader,
  GroupPanelTitle,
  GroupPanelCount,
  GroupScrollArea,
  GroupRow,
  GroupRowName,
  GroupRowCount,
  GroupRowActions,
  GroupRowIconButton,
  NewGroupButton,
  InlineInputWrap,
  InlineInput,
  InlineInputAction,
  MemberPanel,
  MemberPanelHeader,
  MemberPanelTitleRow,
  MemberPanelTitle,
  MemberPanelCount,
  EditMembersButton,
  MemberScrollArea,
  MemberRowRemove,
  MemberTable,
  MemberTableHead,
  MemberTableHeadCell,
  MemberTableRow,
  MemberTableCell,
  MemberTableName,
  SortArrow,
  TicketScroller,
  MemberSearchField,
  MemberFilterChip,
  FilterIconButton,
  MemberActionBar,
  MemberActionCount,
  MemberActionButton,
  MemberActionClear,
  EmptyState,
  EmptyTitle,
  EmptyDescription,
  PanelTabs,
  PanelTab,
  TabsRow,
  SearchPreview,
  SearchPreviewRow,
  SearchPreviewEmpty,
} from './MemberGroupModalStyled';

// 표 컬럼 정의. 정렬 키는 아래 SORT_VALUE 와 짝을 이룬다.
const MEMBER_COLUMNS = [
  { key: 'memberType', label: '회원 구분', width: '72px' },
  { key: 'name', label: '이름', width: '90px' },
  { key: 'gender', label: '성별', width: '52px' },
  { key: 'age', label: '나이', width: '48px' },
  { key: 'tickets', label: '상품 정보', grow: true },
];

// 회원 구분은 가나다가 아니라 회원목록 탭과 같은 의미 순서로 세운다.
const MEMBER_TYPE_ORDER = [
  '유효회원',
  '사용예정',
  '만료임박',
  '중지회원',
  '미수회원',
  '라커만료',
  '만료회원',
];

// 그룹은 수십 명 규모라 한 번에 받아 클라이언트에서 정렬한다.
const MEMBER_ROW_LIMIT = 1000;

// 한 번에 그리는 행 수. 1000행을 통째로 얹으면 체크 하나 누를 때마다 전부 다시 그린다.
const MEMBER_RENDER_CHUNK = 120;

const CHECKBOX_SX = { padding: 0, color: '#C4CACE', '&.Mui-checked': { color: '#4AB3BC' }, '&.MuiCheckbox-indeterminate': { color: '#4AB3BC' } };

// useQuery data 기본값. 인라인 [] 는 렌더마다 새 참조라 useMemo 의존성이 매번 깨진다.
const EMPTY_ROWS = [];

const SORT_VALUE = {
  memberType: row => {
    const index = MEMBER_TYPE_ORDER.indexOf(row.memberType);
    return index === -1 ? MEMBER_TYPE_ORDER.length : index;
  },
  name: row => row.name || '',
  gender: row => row.gender || '',
  // 나이는 문자열로 오고 빈 값도 있다. 없는 값은 뒤로 보낸다.
  age: row => (row.age === '' || row.age == null ? Number.MAX_SAFE_INTEGER : Number(row.age)),
  // 상품 정보는 글로 비교할 게 없어 보유 상품 수로 센다.
  tickets: row => row.tickets?.length || 0,
};

/**
 * 회원 그룹 관리 모달.
 * 왼쪽에서 그룹을 만들고 고르면, 오른쪽에서 그 그룹의 회원과 출결을 본다.
 * 소속 회원을 넣고 빼는 편집은 '회원 편집' 버튼으로 여는 별도 모달이 맡는다.
 */
const MemberGroupModal = ({ open, onClose }) => {
  const { t } = useTranslation();
  const toast = useToast();
  const user = useSelector(state => state.user.user);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const branchId = user?.branch?.branchId;

  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [createName, setCreateName] = useState('');
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [editName, setEditName] = useState('');
  const [renameTarget, setRenameTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null); // 삭제 확인 대상 그룹
  const [rightTab, setRightTab] = useState('members'); // 오른쪽 패널 탭 (members | attendance)
  // 출결 검색은 회원 검색과 같은 자리(제목행)에 두므로 상태도 여기서 들고 있는다.
  const [attendanceKeyword, setAttendanceKeyword] = useState('');
  // 그룹 단위 문자·엑셀. 회원목록과 같은 권한 키를 쓴다.
  const canSMS = useUserAuth('MEMBER_SMS');
  const canExcel = useUserAuth('MEMBER_EXCEL_DOWNLOAD');
  const [smsOpen, setSmsOpen] = useState(false);
  const [pointOpen, setPointOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false); // 소속 회원 편집 모달
  const [transferOpen, setTransferOpen] = useState(false); // 그룹 간 회원 이동 모달

  /*
   * 지점 포인트 설정. 적립이 꺼진 지점에서는 포인트 버튼을 숨긴다(사용자 확정).
   * 서버도 같은 조건으로 거부하므로 여기서는 노출만 정리한다.
   */
  const { data: pointSetting } = useQuery({
    queryKey: ['branchPointSetting', branchId],
    queryFn: () => getBranchPointSetting(branchId, user.token),
    enabled: open && !!branchId && !!user?.token,
  });
  const pointEnabled = pointSetting?.enabledYn === 'Y';

  // 그룹 목록 (회원수 포함)
  const { data: groups = [], isLoading: groupsLoading } = useQuery({
    queryKey: ['memberGroups', branchId],
    queryFn: () => getMemberGroups(user.token, branchId),
    enabled: open && !!branchId && !!user?.token,
  });

  // 선택 그룹의 회원 목록. 소속의 정본이라 회원수·편집 모달 기준으로 쓴다.
  const { data: groupMembers = [], isLoading: membersLoading } = useQuery({
    queryKey: ['memberGroupMembers', selectedGroupId],
    queryFn: () => getMemberGroupMembers(user.token, selectedGroupId),
    enabled: open && !!selectedGroupId,
  });

  // 조건은 회원목록 상세필터와 같은 키를 쓴다. 적용을 눌러야 conditions 로 옮겨 붙는다.
  const [conditions, setConditions] = useState(EMPTY_CONDITIONS);
  const [draftConditions, setDraftConditions] = useState(EMPTY_CONDITIONS);
  const [filterOpen, setFilterOpen] = useState(false);
  const [rowRenderCount, setRowRenderCount] = useState(MEMBER_RENDER_CHUNK);
  const [searchInput, setSearchInput] = useState('');
  const [keyword, setKeyword] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setKeyword(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);


  // 조건 패널의 운동 종목 칩은 지점이 실제로 쓰는 종목만 만든다.
  const { data: sportTypes = EMPTY_ROWS } = useQuery({
    queryKey: ['memberGroupSportTypes', user?.branch?.centerId],
    queryFn: async () => {
      const response = await centersDetail(user?.token, user?.branch?.centerId);
      return response?.branchDetailInfo?.sportTypes || [];
    },
    enabled: open && !!user?.token && !!user?.branch?.centerId,
  });

  // 표에 뿌릴 행. 회원 구분·상품 정보는 회원목록 쿼리가 계산하는 값이라 그룹 필터로 그대로 받아 쓴다.
  const { data: memberRows = EMPTY_ROWS, isLoading: rowsLoading } = useQuery({
    queryKey: ['memberGroupMemberRows', selectedGroupId, branchId, keyword, conditions],
    queryFn: async () => {
      const queryString = QueryString.stringify({
        page: 1,
        limit: MEMBER_ROW_LIMIT,
        headBranchId: user.headBranch?.headBranchId,
        branchId,
        memberGroupId: selectedGroupId,
        searchInput: keyword,
        ...conditions,
        sortBy: 'name',
        sortDirection: 'ASC',
      });
      const result = await getUsersList(user.token, queryString);
      return result?.content || [];
    },
    enabled: open && !!selectedGroupId && !!branchId,
    // 검색어·조건이 바뀔 때마다 새 쿼리라 로딩으로 떨어진다. 이전 결과를 유지해 표가 깜빡이지 않게 한다.
    placeholderData: previous => previous,
  });

  const conditionCount = countConditions(conditions);

  const [sort, setSort] = useState({ key: 'name', asc: true });

  // 검색·조건이 바뀌면 표에 없는 행이 선택으로 남는다. 액션바가 화면 밖 회원을 건드리게 된다.
  useEffect(() => {
    setCheckedIds(new Set());
  }, [keyword, conditions]);

  // 그룹을 옮기면 검색·조건·선택은 새로 시작한다.
  useEffect(() => {
    setSearchInput('');
    setKeyword('');
    setAttendanceKeyword('');
    setConditions(EMPTY_CONDITIONS);
    setDraftConditions(EMPTY_CONDITIONS);
    setFilterOpen(false);
    setCheckedIds(new Set());
  }, [selectedGroupId]);

  // 목록이 바뀌면 처음부터 다시 그린다. (sortedRows·sort 선언 뒤에 둬야 한다)
  useEffect(() => {
    setRowRenderCount(MEMBER_RENDER_CHUNK);
  }, [selectedGroupId, keyword, conditions, sort]);

  const handleRowScroll = event => {
    if (rowRenderCount >= sortedRows.length) return;
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 200) {
      setRowRenderCount(prev => prev + MEMBER_RENDER_CHUNK);
    }
  };

  const toggleSort = key =>
    setSort(prev => (prev.key === key ? { key, asc: !prev.asc } : { key, asc: true }));

  const sortedRows = useMemo(() => {
    const pick = SORT_VALUE[sort.key];
    return [...memberRows].sort((a, b) => {
      const left = pick(a);
      const right = pick(b);
      const diff =
        typeof left === 'string' ? left.localeCompare(right, 'ko') : left - right;
      return sort.asc ? diff : -diff;
    });
  }, [memberRows, sort]);

  // 상품 정보는 스크롤바 없이 끌어서 본다. 끌고 난 직후의 클릭은 회원 상세 이동으로 새지 않게 막는다.
  const ticketDragRef = useRef({ active: false, startX: 0, startScroll: 0, moved: false });

  const startTicketDrag = event => {
    const el = event.currentTarget;
    ticketDragRef.current = {
      active: true,
      startX: event.clientX,
      startScroll: el.scrollLeft,
      moved: false,
      el,
    };
  };

  const moveTicketDrag = event => {
    const drag = ticketDragRef.current;
    if (!drag.active) return;
    const delta = event.clientX - drag.startX;
    if (Math.abs(delta) > 3) drag.moved = true;
    drag.el.scrollLeft = drag.startScroll - delta;
  };

  const endTicketDrag = () => {
    ticketDragRef.current.active = false;
  };

  const swallowClickAfterDrag = event => {
    if (!ticketDragRef.current.moved) return;
    ticketDragRef.current.moved = false;
    event.stopPropagation();
  };

  const goToMember = memberId => {
    onClose();
    navigate(`/member/${memberId}`);
  };

  // ===== 표 선택 =====
  const [checkedIds, setCheckedIds] = useState(() => new Set());
  const [moveAnchor, setMoveAnchor] = useState(null);

  const toggleChecked = memberId =>
    setCheckedIds(prev => {
      const next = new Set(prev);
      if (next.has(memberId)) next.delete(memberId);
      else next.add(memberId);
      return next;
    });

  // 헤더 체크박스는 지금 보이는(=조건에 걸러진) 행만 다룬다.
  const allChecked = sortedRows.length > 0 && sortedRows.every(row => checkedIds.has(row.memberId));
  const someChecked = sortedRows.some(row => checkedIds.has(row.memberId));

  const toggleAllChecked = () =>
    setCheckedIds(prev => {
      if (allChecked) {
        const next = new Set(prev);
        sortedRows.forEach(row => next.delete(row.memberId));
        return next;
      }
      return new Set([...prev, ...sortedRows.map(row => row.memberId)]);
    });

  const checkedRows = useMemo(
    () => sortedRows.filter(row => checkedIds.has(row.memberId)),
    [sortedRows, checkedIds],
  );

  const selectedGroup = useMemo(
    () => groups.find(g => g.id === selectedGroupId) || null,
    [groups, selectedGroupId],
  );

  // 그룹 목록이 바뀌었을 때 선택 그룹이 사라졌으면(삭제 등) 첫 그룹으로 이동.
  // 목록이 비면 선택도 비워야 삭제된 그룹 ID로 회원을 재조회(404)하지 않는다.
  useEffect(() => {
    if (!open) return;
    if (groups.length === 0) {
      if (selectedGroupId !== null) setSelectedGroupId(null);
      return;
    }
    if (!groups.some(g => g.id === selectedGroupId)) {
      setSelectedGroupId(groups[0].id);
    }
  }, [open, groups, selectedGroupId]);

  // 모달 닫힐 때 입력 상태 초기화 (선택 그룹은 유지해 재오픈 시 이어서 작업)
  useEffect(() => {
    if (open) return;
    setCreating(false);
    setCreateName('');
    setEditingGroupId(null);
    // 이 모달은 회원목록에 상시 마운트라 닫아도 상태가 남는다. 다시 열었을 때
    // 지난 검색·조건과 체크(=액션바)가 그대로 살아 있으면 오조작으로 이어진다.
    setSearchInput('');
    setKeyword('');
    setAttendanceKeyword('');
    setConditions(EMPTY_CONDITIONS);
    setDraftConditions(EMPTY_CONDITIONS);
    setFilterOpen(false);
    setCheckedIds(new Set());
  }, [open]);

  const refreshGroups = () => {
    queryClient.invalidateQueries({ queryKey: ['memberGroups', branchId] });
    queryClient.invalidateQueries({ queryKey: ['memberGroupMembers'] });
    // 표는 회원목록 조회를 따로 타므로 같이 갱신해야 추가/제외가 바로 보인다.
    queryClient.invalidateQueries({ queryKey: ['memberGroupMemberRows'] });
    // 회원 상세의 소속 그룹 칩도 같이 턴다. 안 그러면 방금 뺀 그룹이 남아 보인다.
    queryClient.invalidateQueries({ queryKey: ['memberGroupsOfMember'] });
  };

  // ===== 그룹 CRUD =====
  const createMutation = useMutation({
    mutationFn: name => createMemberGroup(user.token, { branchId, name: name.trim() }),
    onSuccess: () => {
      toast.success(t('그룹이 생성되었습니다'));
      setCreating(false);
      setCreateName('');
      refreshGroups();
    },
    onError: error => toast.error(errorMessages(error)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ groupId, name }) => updateMemberGroup(user.token, groupId, { name: name.trim() }),
    onSuccess: () => {
      setEditingGroupId(null);
      refreshGroups();
    },
    onError: error => toast.error(errorMessages(error)),
  });

  const deleteMutation = useMutation({
    mutationFn: groupId => deleteMemberGroup(user.token, groupId),
    onSuccess: (result, groupId) => {
      toast.success(t('그룹이 삭제되었습니다'));
      setDeleteTarget(null);
      // 그룹 캐시에서 삭제된 행을 즉시 제거한다. 재조회가 끝나기 전의 stale 캐시를
      // 자동선택 effect가 읽으면 방금 삭제한 그룹을 재선택해 404 조회가 났다 (QA 재현).
      queryClient.setQueryData(['memberGroups', branchId], old =>
        (old || []).filter(g => g.id !== groupId),
      );
      if (groupId === selectedGroupId) {
        const remaining = groups.filter(g => g.id !== groupId);
        setSelectedGroupId(remaining.length > 0 ? remaining[0].id : null);
      }
      queryClient.invalidateQueries({ queryKey: ['memberGroups', branchId] });
    },
    onError: error => toast.error(errorMessages(error)),
  });

  // ===== 회원 제거 (행 단위 빠른 제외) =====
  /*
   * 검색 미리보기 — 치는 동안 위에서 바로 집어 가라고 최대 8개만 보여준다.
   * 더 많으면 아래 표가 이미 다 걸러 놓았으니 거기서 보면 된다.
   */
  const searchPreview = useMemo(() => {
    const trimmed = searchInput.trim();
    if (!trimmed) return [];
    const lowered = trimmed.toLowerCase();
    return groupMembers
      .filter(row => (row.name || '').toLowerCase().includes(lowered)
        || (row.phoneNumber || '').replace(/-/g, '').includes(lowered.replace(/-/g, '')))
      .slice(0, 8);
  }, [groupMembers, searchInput]);

  const removeMutation = useMutation({
    mutationFn: memberIds => removeMemberGroupMembers(user.token, selectedGroupId, memberIds),
    onSuccess: (_data, memberIds) => {
      // 행이 사라지는 것 말고는 아무 신호가 없어 오클릭인지 알 수 없었다.
      toast.success(`${memberIds.length}${t('명을 그룹에서 제외했습니다')}`);
      setCheckedIds(new Set());
      refreshGroups();
    },
    onError: error => toast.error(errorMessages(error)),
  });

  // 여러 명을 한 번에 빼는 건 되돌릴 수 없으니 확인을 받는다. 한 명(행 ✕)은 그대로 즉시.
  const [removeTarget, setRemoveTarget] = useState(null);

  /*
   * 중복 생성 가드는 ref 여야 한다. createMutation.isPending 은 mutate() 직후가 아니라
   * 다음 렌더에서야 true 가 되므로, 같은 틱에 들어온 두 번째 호출(Enter 연타, Enter + ✓ 클릭)이
   * 여전히 false 를 보고 통과한다. 운영에서 그룹이 2개씩 만들어진 원인이다(2026-08-21,
   * member_group ID 1·2 와 5·6 이 REG_DT 초까지 동일). ref 는 동기적으로 즉시 막힌다.
   */
  const creatingRef = useRef(false);

  const handleCreateSubmit = () => {
    if (!createName.trim() || creatingRef.current) return;
    creatingRef.current = true;
    createMutation.mutate(createName, {
      onSettled: () => {
        creatingRef.current = false;
      },
    });
  };

  const handleEditSubmit = () => {
    if (!editName.trim() || updateMutation.isPending) return;
    const before = groups.find(group => group.id === editingGroupId)?.name ?? '';
    // 이름만 바뀌는 일이지만 다른 그룹 이름으로 잘못 덮어쓰는 사고가 잦아 한 번 묻는다.
    if (before === editName.trim()) {
      setEditingGroupId(null);
      return;
    }
    setRenameTarget({ groupId: editingGroupId, before, after: editName.trim() });
  };

  const startEdit = (event, group) => {
    event.stopPropagation();
    setEditingGroupId(group.id);
    setEditName(group.name);
  };

  /*
   * 문자·엑셀 대상. 체크한 회원이 있으면 그들만, 없으면 지금 표에 보이는 전원이다.
   * 검색·필터를 걸어 좁혀 둔 상태라면 그 결과가 곧 사용자가 보고 있는 대상이다.
   */
  const actionTargets = checkedRows.length ? checkedRows : sortedRows;

  const handleGroupExcel = async () => {
    if (!canExcel || !actionTargets.length) return;
    try {
      // 그룹 엑셀은 지점 컬럼 설정과 무관하게 기본 컬럼 전부를 내린다
      await exportMembersToStyledExcel(
        actionTargets,
        MEMBER_LIST_COLUMNS,
        { t, currentBranchId: user?.branch?.branchId, badgeLabel: null },
        `${selectedGroup?.name || '그룹'}_회원목록`,
      );
    } catch (error) {
      toast.error(t('엑셀 다운로드에 실패했습니다'));
    }
  };

  const askDelete = (event, group) => {
    event.stopPropagation();
    setDeleteTarget(group);
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalOverlay onClick={onClose}>
        <ModalContainer onClick={e => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>{t('회원 그룹 관리')}</ModalTitle>
            <CloseButton onClick={onClose}>
              <CloseIcon />
            </CloseButton>
          </ModalHeader>

          <ModalBody>
            {/* ===== 왼쪽: 그룹 목록 ===== */}
            <GroupPanel>
              <GroupPanelHeader>
                <GroupPanelTitle>{t('그룹')}</GroupPanelTitle>
                <GroupPanelCount>{groups.length}</GroupPanelCount>
              </GroupPanelHeader>
              <GroupScrollArea>
                {creating ? (
                  <InlineInputWrap>
                    <InlineInput
                      autoFocus
                      placeholder={t('그룹 이름')}
                      value={createName}
                      inputProps={{ maxLength: 50 }}
                      onChange={e => setCreateName(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') handleCreateSubmit();
                        if (e.key === 'Escape') setCreating(false);
                      }}
                    />
                    <InlineInputAction className="confirm" onClick={handleCreateSubmit}>
                      {createMutation.isPending ? <CircularProgress size={14} /> : <CheckIcon sx={{ fontSize: 17 }} />}
                    </InlineInputAction>
                    <InlineInputAction onClick={() => setCreating(false)}>
                      <CloseIcon sx={{ fontSize: 17 }} />
                    </InlineInputAction>
                  </InlineInputWrap>
                ) : (
                  <NewGroupButton onClick={() => setCreating(true)} sx={{ marginBottom: '8px' }}>
                    <AddIcon sx={{ fontSize: 17 }} />
                    {t('새 그룹')}
                  </NewGroupButton>
                )}

                {groupsLoading ? (
                  <EmptyState>
                    <CircularProgress size={22} />
                  </EmptyState>
                ) : (
                  groups.map((group, index) =>
                    editingGroupId === group.id ? (
                      <InlineInputWrap key={group.id}>
                        <InlineInput
                          autoFocus
                          value={editName}
                          inputProps={{ maxLength: 50 }}
                          onChange={e => setEditName(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') handleEditSubmit();
                            if (e.key === 'Escape') {
                              // 막지 않으면 MUI Modal 까지 전파돼 이름 편집만 접으려다 창이 통째로 닫힌다.
                              e.stopPropagation();
                              setEditingGroupId(null);
                            }
                          }}
                        />
                        <InlineInputAction className="confirm" onClick={handleEditSubmit}>
                          <CheckIcon sx={{ fontSize: 17 }} />
                        </InlineInputAction>
                        <InlineInputAction onClick={() => setEditingGroupId(null)}>
                          <CloseIcon sx={{ fontSize: 17 }} />
                        </InlineInputAction>
                      </InlineInputWrap>
                    ) : (
                      <GroupRow
                        key={group.id}
                        isSelected={group.id === selectedGroupId}
                        enterIndex={index}
                        onClick={() => setSelectedGroupId(group.id)}>
                        {/* 폭이 좁아 이름이 잘릴 수 있어 올리면 전체 이름을 말풍선으로 보여준다 */}
                        <Tooltip
                          title={group.name}
                          placement="top"
                          arrow
                          enterDelay={300}
                          slotProps={{
                            tooltip: {
                              sx: {
                                fontSize: '13.5px',
                                fontWeight: 600,
                                padding: '8px 12px',
                                maxWidth: '320px',
                                backgroundColor: '#262626',
                              },
                            },
                            arrow: { sx: { color: '#262626' } },
                          }}>
                          <GroupRowName isSelected={group.id === selectedGroupId}>
                            {group.name}
                          </GroupRowName>
                        </Tooltip>
                        <GroupRowActions className="group-row-actions">
                          <GroupRowIconButton onClick={e => startEdit(e, group)}>
                            <EditOutlinedIcon sx={{ fontSize: 15 }} />
                          </GroupRowIconButton>
                          <GroupRowIconButton className="danger" onClick={e => askDelete(e, group)}>
                            <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                          </GroupRowIconButton>
                        </GroupRowActions>
                        <GroupRowCount className="group-row-count">
                          {group.memberCount}
                          {t('명')}
                        </GroupRowCount>
                      </GroupRow>
                    ),
                  )
                )}

                {!groupsLoading && groups.length === 0 && !creating && (
                  <EmptyState sx={{ padding: '40px 8px' }}>
                    <GroupsOutlinedIcon sx={{ fontSize: 34, color: '#ddd' }} />
                    <EmptyTitle>{t('아직 그룹이 없습니다')}</EmptyTitle>
                    <EmptyDescription>
                      {t('필라테스 그룹, 60대 그룹처럼')}
                      <br />
                      {t('자유롭게 만들어 회원을 관리해 보세요')}
                    </EmptyDescription>
                  </EmptyState>
                )}
              </GroupScrollArea>
            </GroupPanel>

            {/* ===== 오른쪽: 선택 그룹의 회원 ===== */}
            <MemberPanel>
              {selectedGroup ? (
                <>
                  <MemberPanelHeader>
                    <MemberPanelTitleRow>
                      <MemberPanelTitle>{selectedGroup.name}</MemberPanelTitle>
                      <MemberPanelCount>
                        {groupMembers.length}
                        {t('명')}
                      </MemberPanelCount>
                      {/* 검색은 그룹명·전체 건수 옆, 모달 맨 윗줄에 둔다 (회원·출결 같은 자리) */}
                      <Box sx={{ position: 'relative', flex: '1 1 auto', minWidth: '260px', maxWidth: '620px', marginLeft: '8px', marginRight: '8px' }}>
                      <MemberSearchField sx={{ width: '100%' }}>
                        <SearchIcon sx={{ fontSize: 16, color: '#B9C0C4' }} />
                        <input
                          value={rightTab === 'members' ? searchInput : attendanceKeyword}
                          onChange={e =>
                            rightTab === 'members'
                              ? setSearchInput(e.target.value)
                              : setAttendanceKeyword(e.target.value)
                          }
                          placeholder={
                            rightTab === 'members'
                              ? t('이름·전화번호로 찾기')
                              : t('회원명·수업명으로 찾기')
                          }
                        />
                        {(rightTab === 'members' ? searchInput : attendanceKeyword) && (
                          <MemberRowRemove
                            sx={{ width: '20px', height: '20px' }}
                            onClick={() =>
                              rightTab === 'members' ? setSearchInput('') : setAttendanceKeyword('')
                            }>
                            <CloseIcon sx={{ fontSize: 14 }} />
                          </MemberRowRemove>
                        )}
                      </MemberSearchField>
                      {/*
                        치는 동안 바로 뜨는 결과. 아래 표가 이미 걸러지긴 하지만 목록이 길면
                        어디가 걸렸는지 눈으로 찾아야 한다 — 위에서 바로 집어 갈 수 있게 한다.
                        출결 탭은 행이 자식 컴포넌트에 있어 아직 회원 탭만 뜬다.
                      */}
                      {rightTab === 'members' && searchInput.trim() && (
                        <SearchPreview>
                          {searchPreview.length === 0 ? (
                            <SearchPreviewEmpty>{t('찾는 회원이 없습니다')}</SearchPreviewEmpty>
                          ) : (
                            searchPreview.map(row => (
                              <SearchPreviewRow
                                key={row.memberId}
                                onClick={() => setSearchInput(row.name || '')}>
                                <span style={{ fontWeight: 700 }}>{row.name || '-'}</span>
                                <span style={{ color: '#8A939C', fontSize: '12.5px' }}>
                                  {row.phoneNumber || '-'}
                                </span>
                              </SearchPreviewRow>
                            ))
                          )}
                        </SearchPreview>
                      )}
                      </Box>
                      {/* 소속 회원을 다 받기 전에 열면 편집 모달이 빈 목록을 기준 소속으로 굳혀 버린다. */}
                      <EditMembersButton
                        sx={{ marginLeft: 'auto', flexShrink: 0 }}
                        disabled={membersLoading}
                        onClick={() => setEditOpen(true)}>
                        <PersonAddAlt1OutlinedIcon sx={{ fontSize: 16 }} />
                        {t('회원 편집')}
                      </EditMembersButton>
                      <EditMembersButton
                        disabled={membersLoading || groups.length < 2}
                        onClick={() => setTransferOpen(true)}>
                        <DriveFileMoveOutlinedIcon sx={{ fontSize: 16 }} />
                        {t('회원 이동')}
                      </EditMembersButton>
                    </MemberPanelTitleRow>
                    {/* 필터는 탭 줄 오른쪽 끝 — 표 바로 위 우측이라 표가 한 줄 위로 올라온다 */}
                    <TabsRow>
                      {/* 출결은 바로 아래 툴바가 붙어야 화면이 안 붕 뜬다. 회원 탭만 아래 여백을 둔다. */}
                      <PanelTabs sx={{ marginBottom: rightTab === 'attendance' ? '8px' : '12px' }}>
                        <PanelTab isActive={rightTab === 'members'} onClick={() => setRightTab('members')}>
                          {t('회원')}
                        </PanelTab>
                        <PanelTab isActive={rightTab === 'attendance'} onClick={() => setRightTab('attendance')}>
                          {t('출결')}
                          <BetaBadge />
                        </PanelTab>
                      </PanelTabs>
                      {rightTab === 'members' && (
                        <>
                          <div style={{ flex: 1 }} />
                          {conditionCount > 0 && (
                            <MemberFilterChip
                              isActive={false}
                              onClick={() => {
                                setConditions(EMPTY_CONDITIONS);
                                setDraftConditions(EMPTY_CONDITIONS);
                              }}>
                              {t('조건 해제')}
                            </MemberFilterChip>
                          )}
                          <FilterIconButton
                            isActive={conditionCount > 0 || filterOpen}
                            onClick={() => {
                              setDraftConditions(conditions);
                              setFilterOpen(prev => !prev);
                            }}>
                            <FilterListIcon sx={{ fontSize: 15 }} />
                            {conditionCount > 0 ? `${t('필터')} ${conditionCount}` : t('필터')}
                          </FilterIconButton>

                          {/*
                            문자·엑셀 — 체크한 회원이 있으면 그들만, 없으면 지금 표에 보이는 전원.
                            회원목록과 같은 권한 키를 쓰고, 없으면 이유를 툴팁으로 밝힌다.
                          */}
                          <Tooltip title={!canSMS ? t('권한이 없습니다') : ''} arrow>
                            <span>
                              <FilterIconButton
                                disabled={!canSMS || !actionTargets.length}
                                onClick={() => setSmsOpen(true)}>
                                <SmsOutlinedIcon sx={{ fontSize: 15 }} />
                                {checkedRows.length
                                  ? `${t('문자')} ${checkedRows.length}`
                                  : t('문자')}
                              </FilterIconButton>
                            </span>
                          </Tooltip>
                          {/* 포인트 일괄 지급 — 대상 규칙은 문자·엑셀과 같다 */}
                          {pointEnabled && (
                            <FilterIconButton
                              disabled={!actionTargets.length}
                              onClick={() => setPointOpen(true)}>
                              <LoyaltyOutlinedIcon sx={{ fontSize: 15 }} />
                              {checkedRows.length
                                ? `${t('포인트')} ${checkedRows.length}`
                                : t('포인트')}
                            </FilterIconButton>
                          )}
                          <Tooltip title={!canExcel ? t('권한이 없습니다') : ''} arrow>
                            <span>
                              <FilterIconButton
                                disabled={!canExcel || !actionTargets.length}
                                onClick={handleGroupExcel}>
                                <FileDownloadOutlinedIcon sx={{ fontSize: 15 }} />
                                {t('엑셀')}
                              </FilterIconButton>
                            </span>
                          </Tooltip>
                        </>
                      )}
                    </TabsRow>
                  </MemberPanelHeader>

                  {rightTab === 'attendance' ? (
                    <MemberScrollArea
                      key={`attendance-${selectedGroup.id}`}
                      /* 출결은 툴바가 바로 오므로 위 여백을 없앤다 — 탭 줄에 붙여 세운다. */
                      sx={{ display: 'flex', flexDirection: 'column', overflowY: 'hidden', paddingTop: '4px' }}>
                      <MemberGroupAttendanceTab
                        group={selectedGroup}
                        members={groupMembers}
                        onNavigate={onClose}
                        keyword={attendanceKeyword}
                      />
                    </MemberScrollArea>
                  ) : (
                  <MemberScrollArea sx={{ padding: '0 12px 20px' }} onScroll={handleRowScroll}>
                    {membersLoading || rowsLoading ? (
                      <EmptyState>
                        <CircularProgress size={22} />
                      </EmptyState>
                    ) : groupMembers.length === 0 ? (
                      <EmptyState>
                        <PersonAddAlt1OutlinedIcon sx={{ fontSize: 34, color: '#ddd' }} />
                        <EmptyTitle>{t('그룹에 회원이 없습니다')}</EmptyTitle>
                        <EmptyDescription>
                          {t("'회원 편집'으로 조건에 맞는 회원을 찾아 담거나,")}
                          <br />
                          {t('회원목록에서 체크한 뒤 그룹 추가로 담을 수 있습니다')}
                        </EmptyDescription>
                      </EmptyState>
                    ) : (
                      <MemberTable>
                        {filterOpen && (
                          <MemberGroupConditionPanel
                            draft={draftConditions}
                            setDraft={setDraftConditions}
                            sportTypes={sportTypes}
                            onReset={() => setDraftConditions(EMPTY_CONDITIONS)}
                            onApply={() => {
                              setConditions(draftConditions);
                              setFilterOpen(false);
                            }}
                          />
                        )}

                        <MemberTableHead>
                          <MemberTableCell w="30px">
                            <Checkbox
                              size="small"
                              sx={CHECKBOX_SX}
                              checked={allChecked}
                              indeterminate={someChecked && !allChecked}
                              onChange={toggleAllChecked}
                            />
                          </MemberTableCell>
                          {MEMBER_COLUMNS.map(column => (
                            <MemberTableHeadCell
                              key={column.key}
                              w={column.width}
                              grow={column.grow}
                              isActive={sort.key === column.key}
                              onClick={() => toggleSort(column.key)}>
                              {t(column.label)}
                              <SortArrow>
                                {sort.key === column.key ? (sort.asc ? '▲' : '▼') : '▽'}
                              </SortArrow>
                            </MemberTableHeadCell>
                          ))}
                          <MemberTableCell w="24px" />
                        </MemberTableHead>

                        {sortedRows.slice(0, rowRenderCount).map((row, index) => (
                          <MemberTableRow
                            key={row.memberId}
                            enterIndex={index}
                            /*
                             * 행 아무 데나 누르면 체크. 상세로는 이름을 눌러야 간다.
                             * 예전처럼 행 전체가 상세 이동이면 체크하려다 화면이 넘어간다.
                             */
                            onClick={() => toggleChecked(row.memberId)}>
                            <MemberTableCell w="30px">
                              <Checkbox
                                size="small"
                                sx={CHECKBOX_SX}
                                checked={checkedIds.has(row.memberId)}
                                // 행 클릭이 이미 토글하므로 체크박스는 표시만 맡는다.
                                onClick={event => event.stopPropagation()}
                                onChange={() => toggleChecked(row.memberId)}
                              />
                            </MemberTableCell>
                            <MemberTableCell w="72px">
                              <MemberStatusBadge memberType={row.memberType}>
                                {row.memberType || '-'}
                              </MemberStatusBadge>
                            </MemberTableCell>
                            <MemberTableName
                              w="90px"
                              className="member-name-link"
                              title={t('회원 상세로 이동')}
                              onClick={event => {
                                event.stopPropagation();
                                goToMember(row.memberId);
                              }}>
                              {row.name || '-'}
                            </MemberTableName>
                            <MemberTableCell w="52px">{row.gender || '-'}</MemberTableCell>
                            <MemberTableCell w="48px">
                              {row.age === '' || row.age == null ? '-' : `${row.age}${t('세')}`}
                            </MemberTableCell>
                            <MemberTableCell grow sx={{ justifyContent: 'flex-start' }}>
                              {row.tickets?.length > 0 ? (
                                <TicketScroller
                                  onMouseDown={startTicketDrag}
                                  onMouseMove={moveTicketDrag}
                                  onMouseUp={endTicketDrag}
                                  onMouseLeave={endTicketDrag}
                                  onClick={swallowClickAfterDrag}>
                                  <TicketInfoCell tickets={row.tickets} />
                                </TicketScroller>
                              ) : (
                                '-'
                              )}
                            </MemberTableCell>
                            <MemberRowRemove
                              className="member-row-remove"
                              title={t('이 그룹에서 제외')}
                              onClick={event => {
                                // 행 클릭(체크 토글)과 겹치지 않게 막는다.
                                event.stopPropagation();
                                if (removeMutation.isPending) return;
                                removeMutation.mutate([row.memberId]);
                              }}>
                              <CloseIcon sx={{ fontSize: 16 }} />
                            </MemberRowRemove>
                          </MemberTableRow>
                        ))}

                        {sortedRows.length === 0 && (
                          <EmptyState sx={{ padding: '36px 8px' }}>
                            <EmptyTitle>
                              {conditionCount > 0 || keyword
                                ? t('조건에 맞는 회원이 없습니다')
                                : t('회원목록 조회 범위에 잡히는 회원이 없습니다')}
                            </EmptyTitle>
                          </EmptyState>
                        )}
                      </MemberTable>
                    )}
                  </MemberScrollArea>
                  )}

                  {/* 선택 액션바 — 목록을 내려도 패널 하단에 붙어 있게 스크롤 영역 밖에 둔다 */}
                  {rightTab === 'members' && checkedRows.length > 0 && (
                    <MemberActionBar>
                      <MemberActionCount>
                        {checkedRows.length}
                        {t('명 선택')}
                      </MemberActionCount>
                      <MemberActionButton onClick={e => setMoveAnchor(e.currentTarget)}>
                        <DriveFileMoveOutlinedIcon sx={{ fontSize: 16 }} />
                        {t('다른 그룹으로')}
                      </MemberActionButton>
                      <MemberActionButton
                        className="danger"
                        disabled={removeMutation.isPending}
                        onClick={() => setRemoveTarget(checkedRows.map(row => row.memberId))}>
                        <CloseIcon sx={{ fontSize: 15 }} />
                        {t('그룹에서 제외')}
                      </MemberActionButton>
                      <MemberActionClear onClick={() => setCheckedIds(new Set())}>
                        {t('선택 해제')}
                      </MemberActionClear>
                    </MemberActionBar>
                  )}
                </>
              ) : (
                <EmptyState>
                  <GroupsOutlinedIcon sx={{ fontSize: 40, color: '#ddd' }} />
                  <EmptyTitle>{t('그룹을 선택해 주세요')}</EmptyTitle>
                  <EmptyDescription>
                    {t('왼쪽에서 그룹을 고르거나 새 그룹을 만들면')}
                    <br />
                    {t('이곳에서 회원을 추가하고 관리할 수 있습니다')}
                  </EmptyDescription>
                </EmptyState>
              )}
            </MemberPanel>
          </ModalBody>

          {/* 선택 회원을 다른 그룹으로 이동/복사 */}
          <GroupMoveCopyPopover
            open={!!moveAnchor}
            anchorEl={moveAnchor}
            onClose={() => setMoveAnchor(null)}
            groups={groups}
            sourceGroup={selectedGroup}
            memberIds={checkedRows.map(row => row.memberId)}
            onDone={() => setCheckedIds(new Set())}
          />

          {/* 그룹 간 회원 이동 — 좌 기존 그룹 / 화살표 / 우 이동할 그룹 */}
          {selectedGroup && (
            <MemberGroupTransferModal
              open={transferOpen}
              onClose={() => setTransferOpen(false)}
              groups={groups}
              sourceGroup={selectedGroup}
              onSaved={refreshGroups}
            />
          )}

          {/* 소속 회원 편집 — 적용을 눌러야 서버에 반영된다 */}
          {selectedGroup && (
            <MemberGroupMemberEditModal
              open={editOpen}
              onClose={() => setEditOpen(false)}
              group={selectedGroup}
              currentMembers={groupMembers}
              onSaved={refreshGroups}
            />
          )}

          {/* 그룹 문자 — 회원목록과 같은 모달을 그대로 쓴다(수신자 편집·템플릿 전부 동일) */}
          <SmsModal
            open={smsOpen}
            onClose={() => setSmsOpen(false)}
            selectedMembers={actionTargets}
          />

          {/* 그룹 포인트 일괄 지급·차감 — 포인트 관리 화면과 같은 모달을 공유한다 */}
          <GroupPointGrantModal
            open={pointOpen}
            onClose={() => setPointOpen(false)}
            groupId={selectedGroupId}
            groupName={selectedGroup?.name}
            branchId={branchId}
            token={user?.token}
            memberIds={checkedRows.map(row => row.memberId)}
            targetCount={actionTargets.length}
          />

          {/* 회원 일괄 제외 확인 — 되돌릴 수 없으므로 명수를 보여주고 묻는다 */}
          <ConfirmModal
            open={!!removeTarget}
            onClose={() => !removeMutation.isPending && setRemoveTarget(null)}
            onConfirm={() => {
              if (removeMutation.isPending) return;
              removeMutation.mutate(removeTarget, { onSettled: () => setRemoveTarget(null) });
            }}
            message={`선택한 ${removeTarget?.length ?? 0}명을 '${selectedGroup?.name ?? ''}' 그룹에서 제외합니다.\n회원 정보와 다른 그룹 소속은 그대로 유지됩니다.`}
            detail={(
              <GroupChangeSummary
                rows={[{
                  label: selectedGroup?.name ?? '그룹 인원',
                  before: `${groupMembers.length}명`,
                  after: `${Math.max(0, groupMembers.length - (removeTarget?.length ?? 0))}명`,
                  tone: 'down',
                }]}
              />
            )}
            highlightText="회원 정보와 다른 그룹 소속은 그대로 유지"
            highlightColor="#0F7F85"
            cancelText="취소"
            confirmText="제외"
            confirmColor="#F44336"
          />

          {/* 그룹 이름 변경 확인 — 다른 그룹 이름으로 덮어쓰는 사고를 막는다 */}
          <ConfirmModal
            open={!!renameTarget}
            onClose={() => !updateMutation.isPending && setRenameTarget(null)}
            onConfirm={() => {
              if (updateMutation.isPending) return;
              updateMutation.mutate(
                { groupId: renameTarget.groupId, name: renameTarget.after },
                { onSettled: () => setRenameTarget(null) },
              );
            }}
            message={'그룹 이름을 바꿉니다.'}
            highlightText="그룹 이름"
            highlightColor="#0F7F85"
            cancelText="취소"
            confirmText="변경"
            confirmColor="#0F7F85"
            detail={(
              <GroupChangeSummary
                rows={[{
                  label: '이름',
                  before: renameTarget?.before ?? '',
                  after: renameTarget?.after ?? '',
                }]}
              />
            )}
          />

          {/* 그룹 삭제 확인 */}
          <ConfirmModal
            open={!!deleteTarget}
            onClose={() => !deleteMutation.isPending && setDeleteTarget(null)}
            onConfirm={() => {
              if (deleteMutation.isPending) return; // 연타 시 두 번째 DELETE 가 404 로 떨어진다
              deleteMutation.mutate(deleteTarget.id);
            }}
            message={`'${deleteTarget?.name}' 그룹을 삭제합니다.\n그룹만 삭제되며 회원 정보는 그대로 유지됩니다.`}
            highlightText="회원 정보는 그대로 유지"
            highlightColor="#0F7F85"
            cancelText="취소"
            confirmText="삭제"
            confirmColor="#F44336"
          />
        </ModalContainer>
      </ModalOverlay>
    </Modal>
  );
};

export default MemberGroupModal;
